import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './TeacherFreeTier.css';

/**
 * TeacherOnboarding — 5-step flow for free-forever teacher tier.
 *
 * Per docs/FREE_TEACHER_TIER_SCOPE.md Day 3.
 *
 * Steps:
 *   1. Welcome (30s)
 *   2. Try Mira yourself (reuses existing GuestTutorPage; opens in new tab)
 *   3. Invite first student (class code primary, email invite secondary)
 *   4. Set class name (optional)
 *   5. Land on TeacherLiteDashboard
 *
 * "Skip for now" is available at every step; abandoning mid-flow is recoverable —
 * teacher can re-enter at /teacher/onboarding any time.
 */

const TeacherOnboarding = ({ user }) => {
  const [step, setStep] = useState(1);
  const [classCode, setClassCode] = useState(null);
  const [studentEmail, setStudentEmail] = useState('');
  const [className, setClassName] = useState('');
  const [inviteMethod, setInviteMethod] = useState('code'); // 'code' | 'email'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Default class name from teacher's display name
  useEffect(() => {
    if (user?.name) {
      setClassName(`${user.name.split(' ').slice(-1)[0]}'s class`);
    }
  }, [user]);

  // Query-param deep-linking into a specific step
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = parseInt(params.get('step'), 10);
    if (stepParam >= 1 && stepParam <= 5) {
      setStep(stepParam);
    }
  }, [location]);

  const skipToEnd = () => navigate('/teacher/dashboard');

  const generateClassCode = async () => {
    setLoading(true);
    setError('');
    try {
      // Day 4 endpoint — stub response if not yet wired
      const response = await axios.post('/api/classroom/generate-class-code', {
        class_name: className || 'My class',
      });
      setClassCode(response.data?.code || null);
    } catch (err) {
      // Day 4 endpoint not yet shipped — show a mock code so onboarding stays usable
      if (err.response?.status === 404) {
        const mock = Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
        setClassCode(mock);
        setError('Note: class-code backend is still in development. Code shown is a preview.');
      } else {
        setError(err.response?.data?.error || 'Could not generate class code. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const sendEmailInvite = async () => {
    if (!studentEmail.trim()) {
      setError('Please enter a student email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/classroom/invite-student-email', {
        student_email: studentEmail.trim(),
        class_name: className || 'My class',
      });
      setStep(4);
    } catch (err) {
      if (err.response?.status === 404) {
        // Day 4 endpoint not yet shipped — continue the flow anyway
        setStep(4);
      } else {
        setError(err.response?.data?.error || 'Could not send invite. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (classCode) {
      navigator.clipboard?.writeText(classCode);
    }
  };

  const copyLink = () => {
    // Marketing host (teachtappy.ai) vs app host (app.teachtappy.ai) —
    // /class-join is served from both so either works for the student.
    const url = `${window.location.origin.replace('app.', '')}/class-join?code=${classCode}`;
    navigator.clipboard?.writeText(url);
  };

  // --------------------------------------------------------------------
  // Step renderers
  // --------------------------------------------------------------------

  const renderStep1 = () => (
    <div className="teacher-step">
      <div className="teacher-step-badge">Step 1 of 5</div>
      <h2 className="teacher-step-title">Welcome to Tappy, {user?.name?.split(' ')[0] || 'teacher'}.</h2>
      <p className="teacher-step-body">
        You're on the Tappy free tier for teachers. One class, up to 30 students,
        unlimited sessions. No credit card required, ever.
      </p>
      <p className="teacher-step-body">
        Before you invite a student, take 2 minutes to try a Mira session yourself.
        You'll see what your students will see, and you'll understand what Tappy can do.
      </p>
      <div className="teacher-step-actions">
        <button
          className="teacher-btn-primary"
          onClick={() => setStep(2)}
        >
          Try a session →
        </button>
        <button className="teacher-btn-ghost" onClick={skipToEnd}>
          Skip for now
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="teacher-step">
      <div className="teacher-step-badge">Step 2 of 5</div>
      <h2 className="teacher-step-title">Try Mira.</h2>
      <p className="teacher-step-body">
        Open Mira in a new tab and ask her anything. We'll preload a photosynthesis
        lesson so you can hear how the voice tutor works without thinking up a topic.
      </p>
      <p className="teacher-step-hint">
        Tip: talk naturally, pause, ask follow-ups, interrupt her. This is what your
        students will experience.
      </p>
      <div className="teacher-step-actions">
        <a
          className="teacher-btn-primary"
          href="/guest-tutor?source=teacher_onboarding&topic=photosynthesis"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Mira in new tab ↗
        </a>
        <button className="teacher-btn-primary" onClick={() => setStep(3)}>
          Continue →
        </button>
        <button className="teacher-btn-ghost" onClick={skipToEnd}>
          Skip for now
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="teacher-step">
      <div className="teacher-step-badge">Step 3 of 5</div>
      <h2 className="teacher-step-title">Invite your first student.</h2>
      <p className="teacher-step-body">
        Pick how you want to add a student. Class code is the recommended default —
        no student email or account needed. Magic-link email works for high schoolers
        with school email.
      </p>

      <div className="teacher-invite-method-toggle">
        <button
          className={`teacher-method-btn${inviteMethod === 'code' ? ' selected' : ''}`}
          onClick={() => setInviteMethod('code')}
        >
          Class code
          <span className="teacher-method-hint">Recommended</span>
        </button>
        <button
          className={`teacher-method-btn${inviteMethod === 'email' ? ' selected' : ''}`}
          onClick={() => setInviteMethod('email')}
        >
          Email invite
        </button>
      </div>

      {inviteMethod === 'code' && (
        <div className="teacher-invite-panel">
          {!classCode ? (
            <>
              <p className="teacher-step-body">
                Generate a 6-character code. Share it with your students — they go to
                the join page, type the code and their first name, and start Tappy immediately.
              </p>
              <button
                className="teacher-btn-primary"
                onClick={generateClassCode}
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate class code'}
              </button>
            </>
          ) : (
            <>
              <div className="teacher-code-display">
                <div className="teacher-code-label">Your class code</div>
                <div className="teacher-code-value">{classCode}</div>
                {/* Projectable QR — students scan instead of typing.
                    Generated client-side; the join URL never leaves the page. */}
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <QRCodeSVG
                    value={`${window.location.origin.replace('app.', '')}/class-join?code=${classCode}`}
                    size={132}
                    fgColor="#1b3f48"
                  />
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    Project this — students scan to join
                  </span>
                </div>
              </div>
              <div className="teacher-code-actions">
                <button className="teacher-btn-secondary" onClick={copyCode}>
                  Copy code
                </button>
                <button className="teacher-btn-secondary" onClick={copyLink}>
                  Copy join link
                </button>
              </div>
              <p className="teacher-step-hint">
                Students go to teachtappy.ai/class-join, enter code + first name, and start.
                No student email or password needed.
              </p>
              <div className="teacher-step-actions" style={{ marginTop: 24 }}>
                <button className="teacher-btn-primary" onClick={() => setStep(4)}>
                  Continue →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {inviteMethod === 'email' && (
        <div className="teacher-invite-panel">
          <p className="teacher-step-body">
            Enter a student email. We'll send a magic link they can click to join your class.
          </p>
          <input
            type="email"
            className="teacher-input"
            placeholder="student@example.edu"
            value={studentEmail}
            onChange={(e) => setStudentEmail(e.target.value)}
          />
          <div className="teacher-step-actions" style={{ marginTop: 16 }}>
            <button
              className="teacher-btn-primary"
              onClick={sendEmailInvite}
              disabled={loading || !studentEmail.trim()}
            >
              {loading ? 'Sending...' : 'Send invite'}
            </button>
          </div>
        </div>
      )}

      {error && <div className="teacher-inline-error">{error}</div>}

      <div className="teacher-step-footer">
        <button className="teacher-btn-ghost" onClick={skipToEnd}>
          Skip for now
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="teacher-step">
      <div className="teacher-step-badge">Step 4 of 5</div>
      <h2 className="teacher-step-title">Name your class.</h2>
      <p className="teacher-step-body">
        Helps you keep things organized if you later add a second class with a paid plan.
      </p>
      <input
        type="text"
        className="teacher-input"
        value={className}
        onChange={(e) => setClassName(e.target.value)}
        placeholder="e.g., Algebra 1 — Period 3"
      />
      <div className="teacher-step-actions" style={{ marginTop: 20 }}>
        <button className="teacher-btn-primary" onClick={() => setStep(5)}>
          Continue →
        </button>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="teacher-step">
      <div className="teacher-step-badge">All set</div>
      <h2 className="teacher-step-title">You're ready.</h2>
      <p className="teacher-step-body">
        Your first student will appear in your dashboard the moment they join with
        the class code. Until then, here's what you'll see once a session happens —
        mastery by topic, misconceptions to address, minutes saved.
      </p>
      <div className="teacher-step-actions">
        <button className="teacher-btn-primary" onClick={() => navigate('/teacher/dashboard')}>
          Go to dashboard →
        </button>
      </div>
    </div>
  );

  // --------------------------------------------------------------------

  return (
    <div className="teacher-onboarding-page">
      <div className="teacher-onboarding-card">
        {/* Progress rail */}
        <div className="teacher-progress-rail">
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              className={`teacher-progress-dot${n < step ? ' done' : ''}${n === step ? ' active' : ''}`}
            />
          ))}
        </div>

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>
    </div>
  );
};

export default TeacherOnboarding;
